import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Dialog, DialogContent, DialogActions, DialogTitle,
  Divider, Grid, Chip, IconButton, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, FormControlLabel, Switch, Collapse,
  CircularProgress, Autocomplete, Paper, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon,
  ExpandLess as ExpandLessIcon, Link as LinkIcon, Email as EmailIcon,
  UploadFile as UploadFileIcon, InsertDriveFileOutlined as FileIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { assetsApi } from '../../services/api';
import type { AssetFormState, AssetCreateRequest, SnackbarState } from '../../types/assets';
import {
  ASSET_TYPE_OPTIONS, GTM_CATEGORY_OPTIONS, DOCUMENT_CATEGORY_OPTIONS,
  CLOUD_SERVICE_OPTIONS, EMPTY_ASSET_FORM, ASSET_SHORT_DESCRIPTION_MAX,
} from '../../types/assets';
import { EMAIL_REGEX, getTextLengthFromHtml, trimText } from '../../utils/assetHelpers';
import { SEARCE_COLORS } from '../../theme';
import RichTextEditor from '../common/RichTextEditor';
import { useUserAutocomplete } from '../../hooks/useUserAutocomplete';
import type { UserAutocomplete } from '../../services/api';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SubmitAssetDialogProps {
  open: boolean;
  onClose: () => void;
  onSnackbar: (state: SnackbarState) => void;
}

/** Per-row: paste a Google URL vs upload a file (Drive / local). */
type DocLinkMode = 'google_url' | 'drive';

const DOC_SHARE_SERVICE_ACCOUNT_WARNING =
  'Kindly share the document to following Service account first : delivery-pulse-bq-reader@solverssuite-pm-tool.iam.gserviceaccount.com';

const DOC_SHARE_SERVICE_ACCOUNT_HINT =
  'Share this document with delivery-pulse-bq-reader@solverssuite-pm-tool.iam.gserviceaccount.com';

const emptyInternalDraft = (): { category: string; mode: DocLinkMode; url: string } => ({
  category: '',
  mode: 'google_url',
  url: '',
});

const emptyExternalDraft = (): { name: string; mode: DocLinkMode; url: string } => ({
  name: '',
  mode: 'google_url',
  url: '',
});

const isLikelyFileReference = (url: string, mode: DocLinkMode): boolean =>
  mode === 'drive' ||
  /storage\.googleapis\.com|searce-assets|\.pdf($|\?)|\.docx?($|\?)|\.zip($|\?)/i.test(url);

const referenceLabel = (url: string): string => {
  if (!url.trim()) return '—';
  try {
    const u = new URL(url);
    const seg = decodeURIComponent(u.pathname.split('/').pop() || '');
    return seg || trimText(url, 56);
  } catch {
    return trimText(url, 56);
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SubmitAssetDialog: React.FC<SubmitAssetDialogProps> = ({ open, onClose, onSnackbar }) => {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<AssetFormState>({ ...EMPTY_ASSET_FORM });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const {
    query: teamMemberInput,
    setQuery: setTeamMemberInputQuery,
    results: teamMemberSuggestions,
    loading: teamMemberSuggestLoading,
    error: teamMemberSuggestError,
  } = useUserAutocomplete();
  const [showTeamMemberInput, setShowTeamMemberInput] = useState(false);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const internalDraftFileRef = useRef<HTMLInputElement | null>(null);
  const externalDraftFileRef = useRef<HTMLInputElement | null>(null);
  /** Parallel to internal_documentation: how each row was added (for TYPE column). */
  const [internalDocModes, setInternalDocModes] = useState<DocLinkMode[]>([]);
  const [externalDocModes, setExternalDocModes] = useState<DocLinkMode[]>([]);
  const [internalAddOpen, setInternalAddOpen] = useState(false);
  const [externalAddOpen, setExternalAddOpen] = useState(false);
  const [internalDraft, setInternalDraft] = useState(emptyInternalDraft);
  const [externalDraft, setExternalDraft] = useState(emptyExternalDraft);
  const [checkingDocAccess, setCheckingDocAccess] = useState<'internal' | 'external' | null>(null);
  const [internalDraftAccessError, setInternalDraftAccessError] = useState<string | null>(null);
  const [externalDraftAccessError, setExternalDraftAccessError] = useState<string | null>(null);

  // ---- Field helpers ----

  const updateField = useCallback(<K extends keyof AssetFormState>(field: K, value: AssetFormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  }, []);

  const openInternalAddPanel = () => {
    setInternalDraft(emptyInternalDraft());
    setInternalDraftAccessError(null);
    setInternalAddOpen(true);
  };

  const cancelInternalAdd = () => {
    setInternalAddOpen(false);
    setInternalDraft(emptyInternalDraft());
    setInternalDraftAccessError(null);
  };

  const ensureDocumentAccessible = async (url: string, mode: DocLinkMode): Promise<string | null> => {
    // Uploaded files go to GCS under our control — skip Drive SA check.
    if (mode === 'drive') return null;
    try {
      const result = await assetsApi.checkDocumentAccess(url);
      if (result.accessible) return null;
      // Always show the original SA email in the UI (do not surface backend SA email variants).
      return DOC_SHARE_SERVICE_ACCOUNT_WARNING;
    } catch {
      return DOC_SHARE_SERVICE_ACCOUNT_WARNING;
    }
  };

  const commitInternalDraft = async () => {
    const cat = internalDraft.category.trim();
    const url = internalDraft.url.trim();
    if (!cat || !url || checkingDocAccess) return;

    setCheckingDocAccess('internal');
    setInternalDraftAccessError(null);
    try {
      const accessError = await ensureDocumentAccessible(url, internalDraft.mode);
      if (accessError) {
        setInternalDraftAccessError(accessError);
        onSnackbar({ open: true, message: accessError, severity: 'error' });
        return;
      }
      setForm(prev => ({
        ...prev,
        internal_documentation: [...prev.internal_documentation, { category: cat, url }],
      }));
      setInternalDocModes(prev => [...prev, internalDraft.mode]);
      cancelInternalAdd();
    } finally {
      setCheckingDocAccess(null);
    }
  };

  const removeInternalDoc = (index: number) => {
    setInternalDocModes(prev => prev.filter((_, i) => i !== index));
    setForm(prev => ({
      ...prev,
      internal_documentation: prev.internal_documentation.filter((_, i) => i !== index),
    }));
  };

  const openExternalAddPanel = () => {
    setExternalDraft(emptyExternalDraft());
    setExternalDraftAccessError(null);
    setExternalAddOpen(true);
  };

  const cancelExternalAdd = () => {
    setExternalAddOpen(false);
    setExternalDraft(emptyExternalDraft());
    setExternalDraftAccessError(null);
  };

  const commitExternalDraft = async () => {
    const name = externalDraft.name.trim();
    const url = externalDraft.url.trim();
    if (!name || !url || checkingDocAccess) return;

    setCheckingDocAccess('external');
    setExternalDraftAccessError(null);
    try {
      const accessError = await ensureDocumentAccessible(url, externalDraft.mode);
      if (accessError) {
        setExternalDraftAccessError(accessError);
        onSnackbar({ open: true, message: accessError, severity: 'error' });
        return;
      }
      setForm(prev => ({
        ...prev,
        external_documentation: [...prev.external_documentation, { name, url }],
      }));
      setExternalDocModes(prev => [...prev, externalDraft.mode]);
      cancelExternalAdd();
    } finally {
      setCheckingDocAccess(null);
    }
  };

  const removeExternalDoc = (index: number) => {
    setExternalDocModes(prev => prev.filter((_, i) => i !== index));
    setForm(prev => ({
      ...prev,
      external_documentation: prev.external_documentation.filter((_, i) => i !== index),
    }));
  };

  const addTeamMember = (value: string, resetInput: (v: string) => void) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setForm(prev => {
      if (prev.team_members.some((e) => e.toLowerCase() === trimmed.toLowerCase())) {
        return prev;
      }
      return { ...prev, team_members: [...prev.team_members, trimmed] };
    });
    resetInput('');
  };

  const extractEmailsFromText = (value: string): string[] => {
    const emailMatches = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) ?? [];
    return emailMatches.map((e) => e.trim());
  };

  const addTeamMembersFromInput = (value: string, resetInput: (v: string) => void): number => {
    const emails = extractEmailsFromText(value);
    if (emails.length === 0) return 0;
    let addedCount = 0;
    setForm((prev) => {
      const existing = new Set(prev.team_members.map((e) => e.toLowerCase()));
      const deduped = [...prev.team_members];
      for (const email of emails) {
        const normalized = email.toLowerCase();
        if (!EMAIL_REGEX.test(email) || existing.has(normalized)) continue;
        existing.add(normalized);
        deduped.push(email);
        addedCount += 1;
      }
      return addedCount > 0 ? { ...prev, team_members: deduped } : prev;
    });
    if (addedCount > 0) resetInput('');
    return addedCount;
  };

  const teamAutocompleteOptions = useMemo(
    () =>
      teamMemberSuggestions.filter(
        (u) => !form.team_members.some((e) => e.toLowerCase() === u.email.toLowerCase()),
      ),
    [teamMemberSuggestions, form.team_members],
  );

  const removeTeamMember = (index: number) => {
    setForm(prev => ({ ...prev, team_members: prev.team_members.filter((_, i) => i !== index) }));
  };

  const uploadDraftFile = async (file: File, kind: 'internal' | 'external') => {
    setUploadingField(`${kind}-draft`);
    try {
      const result = await assetsApi.uploadDocument(file);
      if (kind === 'internal') {
        setInternalDraft(d => ({ ...d, url: result.file_url }));
      } else {
        setExternalDraft(d => ({
          ...d,
          url: result.file_url,
          name: d.name.trim() ? d.name : file.name,
        }));
      }
    } catch {
      onSnackbar({ open: true, message: `Failed to upload "${file.name}". Please try again.`, severity: 'error' });
    } finally {
      setUploadingField(null);
    }
  };

  // ---- Validation ----

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.asset_name.trim()) errors.asset_name = 'Asset name is required';
    const shortDesc = form.short_description.trim();
    if (!shortDesc) {
      errors.short_description = 'Short description is required';
    } else if (shortDesc.length > ASSET_SHORT_DESCRIPTION_MAX) {
      errors.short_description = `Maximum ${ASSET_SHORT_DESCRIPTION_MAX} characters (${shortDesc.length}/${ASSET_SHORT_DESCRIPTION_MAX})`;
    }
    if (!form.asset_type) errors.asset_type = 'Asset type is required';
    if (!form.approx_start_date) errors.approx_start_date = 'Approx. start date is required';
    if (!form.asset_completion_date) errors.asset_completion_date = 'Completion date is required';
    if (form.key_cloud_services.length === 0) errors.key_cloud_services = 'At least one cloud service is required';
    const problemLen = getTextLengthFromHtml(form.problem_solved);
    if (problemLen < 400) errors.problem_solved = `Minimum 400 characters required (${problemLen}/400)`;
    const valuePropLen = getTextLengthFromHtml(form.primary_value_prop);
    if (valuePropLen < 400) errors.primary_value_prop = `Minimum 400 characters required (${valuePropLen}/400)`;
    if (!form.target_customers_industries.trim()) errors.target_customers_industries = 'Target customers is required';

    const validDocs = form.internal_documentation.filter(d => d.category && d.url.trim());
    if (validDocs.length === 0) errors.internal_documentation = 'At least one internal document is required';

    if (form.marketplace_listing_status && !form.marketplace_url.trim()) {
      errors.marketplace_url = 'URL is required when marketplace listing is enabled';
    }

    if (form.team_members.length === 0) {
      errors.team_members = 'At least one team member email is required';
    } else {
      const invalidEmails = form.team_members.filter(e => !EMAIL_REGEX.test(e));
      if (invalidEmails.length > 0) errors.team_members = `Invalid email(s): ${invalidEmails.join(', ')}`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ---- Submit ----

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload: AssetCreateRequest = {
        asset_name: form.asset_name.trim(),
        short_description: form.short_description.trim(),
        asset_type: form.asset_type,
        approx_start_date: form.approx_start_date,
        asset_completion_date: form.asset_completion_date,
        gtm_category: form.gtm_category || null,
        key_cloud_services: form.key_cloud_services,
        problem_solved: form.problem_solved,
        primary_value_prop: form.primary_value_prop,
        target_customers_industries: form.target_customers_industries.trim(),
        internal_documentation: form.internal_documentation.filter(d => d.category && d.url.trim()),
        external_documentation: form.external_documentation.filter(d => d.name.trim() && d.url.trim()).length > 0
          ? form.external_documentation.filter(d => d.name.trim() && d.url.trim())
          : undefined,
        marketplace_listing_status: form.marketplace_listing_status,
        marketplace_url: form.marketplace_listing_status ? form.marketplace_url.trim() : null,
        pricing_model: form.pricing_model.trim() || null,
        gitlab_repo_url: form.gitlab_repo_url.trim() || null,
        pr_code: form.pr_code.trim() || null,
        team_members: form.team_members,
      };

      const created = await assetsApi.createAsset(payload);
      queryClient.invalidateQueries({ queryKey: ['searce-assets'] });
      onSnackbar({
        open: true,
        message: `Asset ${created.asset_code} submitted successfully!`,
        severity: 'success',
      });
      handleClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to submit asset. Please try again.';
      onSnackbar({ open: true, message: typeof detail === 'string' ? detail : JSON.stringify(detail), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Open / close ----

  const handleClose = () => {
    setFormErrors({});
    onClose();
  };

  const handleEnter = () => {
    setForm({ ...EMPTY_ASSET_FORM });
    setInternalDocModes([]);
    setExternalDocModes([]);
    setInternalAddOpen(false);
    setExternalAddOpen(false);
    setInternalDraft(emptyInternalDraft());
    setExternalDraft(emptyExternalDraft());
    setInternalDraftAccessError(null);
    setExternalDraftAccessError(null);
    setCheckingDocAccess(null);
    setFormErrors({});
    setTeamMemberInputQuery('');
    setShowTeamMemberInput(false);
  };

  // ---- Render ----

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      TransitionProps={{ onEnter: handleEnter }}
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Submit New Asset</Typography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2.5}>

          {/* Asset Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required label="Asset Name"
              value={form.asset_name}
              onChange={e => updateField('asset_name', e.target.value)}
              error={!!formErrors.asset_name}
              helperText={formErrors.asset_name}
            />
          </Grid>

          {/* Asset Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={!!formErrors.asset_type}>
              <InputLabel>Asset Type</InputLabel>
              <Select value={form.asset_type} label="Asset Type" onChange={e => updateField('asset_type', e.target.value)}>
                {ASSET_TYPE_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </Select>
              {formErrors.asset_type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.asset_type}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Short description (shown on asset cards) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Short Description"
              multiline
              minRows={2}
              maxRows={3}
              value={form.short_description}
              onChange={e => {
                const next = e.target.value.slice(0, ASSET_SHORT_DESCRIPTION_MAX);
                updateField('short_description', next);
              }}
              error={!!formErrors.short_description}
              helperText={
                formErrors.short_description
                || `${form.short_description.length}/${ASSET_SHORT_DESCRIPTION_MAX} characters — shown on the asset card under the title`
              }
              inputProps={{ maxLength: ASSET_SHORT_DESCRIPTION_MAX }}
            />
          </Grid>

          {/* Approx. start date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Approx. Start Date"
              type="date"
              value={form.approx_start_date}
              onChange={e => updateField('approx_start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!formErrors.approx_start_date}
              helperText={formErrors.approx_start_date || 'When work on this asset roughly began'}
            />
          </Grid>

          {/* Completion Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth required label="Asset Completion Date" type="date"
              value={form.asset_completion_date}
              onChange={e => updateField('asset_completion_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!formErrors.asset_completion_date}
              helperText={formErrors.asset_completion_date}
            />
          </Grid>

          {/* GTM Category */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>GTM Category</InputLabel>
              <Select value={form.gtm_category} label="GTM Category" onChange={e => updateField('gtm_category', e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {GTM_CATEGORY_OPTIONS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Key Cloud Services */}
          <Grid item xs={12}>
            <Autocomplete
              multiple freeSolo
              options={CLOUD_SERVICE_OPTIONS.filter(o => !form.key_cloud_services.includes(o))}
              value={form.key_cloud_services}
              onChange={(_, newValue) => updateField('key_cloud_services', newValue as string[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Key Cloud Services *"
                  placeholder={form.key_cloud_services.length === 0 ? 'Select or type a custom service...' : 'Add more...'}
                  error={!!formErrors.key_cloud_services}
                  helperText={formErrors.key_cloud_services}
                />
              )}
            />
          </Grid>

          {/* Problem Solved */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: 'text.primary' }}>
              Problem Solved / Core Purpose <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <RichTextEditor
              value={form.problem_solved}
              onChange={value => updateField('problem_solved', value)}
              placeholder="Describe the problem this asset addresses..."
              error={!!formErrors.problem_solved}
              helperText={formErrors.problem_solved || `${getTextLengthFromHtml(form.problem_solved)}/400 characters (min 400)`}
              minHeight={180}
            />
          </Grid>

          {/* Primary Value Prop */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, color: 'text.primary' }}>
              Primary Value Proposition <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <RichTextEditor
              value={form.primary_value_prop}
              onChange={value => updateField('primary_value_prop', value)}
              placeholder="Describe the core value proposition..."
              error={!!formErrors.primary_value_prop}
              helperText={formErrors.primary_value_prop || `${getTextLengthFromHtml(form.primary_value_prop)}/400 characters (min 400)`}
              minHeight={180}
            />
          </Grid>

          {/* Target Customers */}
          <Grid item xs={12}>
            <TextField
              fullWidth required label="Target Customers / Industries"
              value={form.target_customers_industries}
              onChange={e => updateField('target_customers_industries', e.target.value)}
              error={!!formErrors.target_customers_industries}
              helperText={formErrors.target_customers_industries}
            />
          </Grid>

          {/* Internal Documentation */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.25 }}>
              Internal documentation <Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Link Google Docs or upload files. At least one required.
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(100px, 1fr) 100px minmax(0, 2fr) 40px' },
                  gap: 1,
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha('#ffffff', 1),
                }}
              >
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.06, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                  Category
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.06, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                  Type
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.06, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                  Reference
                </Typography>
                <Box />
              </Box>
              {form.internal_documentation.length === 0 ? (
                <Box sx={{ px: 1.5, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">No documents added yet.</Typography>
                </Box>
              ) : (
                form.internal_documentation.map((doc, i) => {
                  const mode = internalDocModes[i] ?? 'google_url';
                  const isFile = isLikelyFileReference(doc.url, mode);
                  return (
                    <Box
                      key={`int-${i}-${doc.url}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'minmax(100px, 1fr) 100px minmax(0, 2fr) 40px' },
                        gap: 1,
                        alignItems: 'center',
                        px: 1.5,
                        py: 1.25,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Chip
                        label={doc.category || '—'}
                        size="small"
                        sx={{
                          justifyContent: 'flex-start',
                          width: 'fit-content',
                          maxWidth: '100%',
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        {isFile ? <FileIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                        <Typography variant="body2" noWrap>{isFile ? 'File' : 'URL'}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word', minWidth: 0 }} title={doc.url}>
                        {referenceLabel(doc.url)}
                      </Typography>
                      <IconButton size="small" onClick={() => removeInternalDoc(i)} color="error" aria-label="Remove document">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={openInternalAddPanel}
              sx={{ mt: 1.5, textTransform: 'none', borderColor: SEARCE_COLORS.primary, color: SEARCE_COLORS.primary }}
            >
              Add document
            </Button>

            {internalAddOpen && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: 2,
                  borderColor: alpha(SEARCE_COLORS.primary, 0.35),
                  bgcolor: alpha(SEARCE_COLORS.primary, 0.02),
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>Add internal document</Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0, pt: 1 }}>Category</Typography>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                    <InputLabel>Select category</InputLabel>
                    <Select
                      label="Select category"
                      value={internalDraft.category}
                      onChange={e => setInternalDraft(d => ({ ...d, category: e.target.value }))}
                    >
                      <MenuItem value=""><em>Select category</em></MenuItem>
                      {DOCUMENT_CATEGORY_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0 }}>Source</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={internalDraft.mode}
                    onChange={(_, v) => v != null && setInternalDraft(d => {
                      setInternalDraftAccessError(null);
                      return { ...d, mode: v as DocLinkMode, url: '' };
                    })}
                    sx={{ flex: 1, flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="google_url" sx={{ textTransform: 'none', gap: 0.5 }}>
                      <LinkIcon fontSize="small" /> Google Doc URL
                    </ToggleButton>
                    <ToggleButton value="drive" sx={{ textTransform: 'none', gap: 0.5 }}>
                      <UploadFileIcon fontSize="small" /> Upload file
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 0, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0, pt: 1 }}>Reference</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {internalDraft.mode === 'google_url' ? (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Paste Google Doc URL (permission: anyone in Searce)"
                        value={internalDraft.url}
                        onChange={e => {
                          setInternalDraftAccessError(null);
                          setInternalDraft(d => ({ ...d, url: e.target.value }));
                        }}
                        error={!!internalDraftAccessError}
                        helperText={internalDraftAccessError || undefined}
                        InputProps={{ startAdornment: <LinkIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: '1rem' }} /> }}
                      />
                    ) : (
                      <Box>
                        <input
                          type="file"
                          hidden
                          ref={el => { internalDraftFileRef.current = el; }}
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) uploadDraftFile(file, 'internal');
                            e.target.value = '';
                          }}
                        />
                        <Box
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') internalDraftFileRef.current?.click(); }}
                          onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                          onDrop={e => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (file) uploadDraftFile(file, 'internal');
                          }}
                          onClick={() => internalDraftFileRef.current?.click()}
                          sx={{
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            py: 3,
                            px: 2,
                            textAlign: 'center',
                            cursor: uploadingField === 'internal-draft' ? 'wait' : 'pointer',
                            bgcolor: 'background.paper',
                            '&:hover': { borderColor: alpha(SEARCE_COLORS.primary, 0.5) },
                          }}
                        >
                          {uploadingField === 'internal-draft' ? (
                            <CircularProgress size={28} />
                          ) : (
                            <>
                              <CloudUploadIcon sx={{ fontSize: 36, color: 'action.active', mb: 0.5 }} />
                              <Typography variant="body2" color="text.secondary">
                                Click to choose file or drag and drop
                              </Typography>
                            </>
                          )}
                        </Box>
                        {internalDraft.url.trim() ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }} title={internalDraft.url}>
                            {referenceLabel(internalDraft.url)}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="black"
                    sx={{ fontWeight: 600, lineHeight: 1.4 }}
                  >
                    {DOC_SHARE_SERVICE_ACCOUNT_HINT}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button variant="outlined" size="small" onClick={cancelInternalAdd} disabled={checkingDocAccess === 'internal'}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={
                        !internalDraft.category.trim()
                        || !internalDraft.url.trim()
                        || uploadingField === 'internal-draft'
                        || checkingDocAccess === 'internal'
                      }
                      onClick={commitInternalDraft}
                      startIcon={checkingDocAccess === 'internal' ? <CircularProgress size={14} color="inherit" /> : undefined}
                      sx={{ bgcolor: SEARCE_COLORS.primary, '&:hover': { bgcolor: SEARCE_COLORS.primaryDark } }}
                    >
                      {checkingDocAccess === 'internal' ? 'Checking access…' : 'Add document'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}

            {formErrors.internal_documentation && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>{formErrors.internal_documentation}</Typography>
            )}
          </Grid>

          {/* External Documentation */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.25 }}>
              External documentation
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Optional links with a display name. Use URL or upload a file.
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#ffffff',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(100px, 1fr) 100px minmax(0, 2fr) 40px' },
                  gap: 1,
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.06, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                  Name
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.06, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                  Type
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.secondary', letterSpacing: 0.06, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                  Reference
                </Typography>
                <Box />
              </Box>
              {form.external_documentation.length === 0 ? (
                <Box sx={{ px: 1.5, py: 2 }}>
                  <Typography variant="body2" color="text.secondary">No external documents added.</Typography>
                </Box>
              ) : (
                form.external_documentation.map((doc, i) => {
                  const mode = externalDocModes[i] ?? 'google_url';
                  const isFile = isLikelyFileReference(doc.url, mode);
                  return (
                    <Box
                      key={`ext-${i}-${doc.url}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'minmax(100px, 1fr) 100px minmax(0, 2fr) 40px' },
                        gap: 1,
                        alignItems: 'center',
                        px: 1.5,
                        py: 1.25,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>{doc.name || '—'}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        {isFile ? <FileIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                        <Typography variant="body2" noWrap>{isFile ? 'File' : 'URL'}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', minWidth: 0 }} title={doc.url}>
                        {referenceLabel(doc.url)}
                      </Typography>
                      <IconButton size="small" onClick={() => removeExternalDoc(i)} color="error" aria-label="Remove document">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={openExternalAddPanel}
              sx={{ mt: 1.5, textTransform: 'none', borderColor: SEARCE_COLORS.primary, color: SEARCE_COLORS.primary }}
            >
              Add document
            </Button>

            {externalAddOpen && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: 2,
                  borderColor: alpha(SEARCE_COLORS.primary, 0.35),
                  bgcolor: alpha(SEARCE_COLORS.primary, 0.02),
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>Add external document</Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0, pt: 1 }}>Name</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. Blog post"
                    value={externalDraft.name}
                    onChange={e => setExternalDraft(d => ({ ...d, name: e.target.value }))}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0 }}>Source</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={externalDraft.mode}
                    onChange={(_, v) => v != null && setExternalDraft(d => {
                      setExternalDraftAccessError(null);
                      return { ...d, mode: v as DocLinkMode, url: '' };
                    })}
                    sx={{ flex: 1, flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="google_url" sx={{ textTransform: 'none', gap: 0.5 }}>
                      <LinkIcon fontSize="small" /> Google Doc URL
                    </ToggleButton>
                    <ToggleButton value="drive" sx={{ textTransform: 'none', gap: 0.5 }}>
                      <UploadFileIcon fontSize="small" /> Upload file
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 0, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 88, flexShrink: 0, pt: 1 }}>Reference</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {externalDraft.mode === 'google_url' ? (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Paste public or shared link"
                        value={externalDraft.url}
                        onChange={e => {
                          setExternalDraftAccessError(null);
                          setExternalDraft(d => ({ ...d, url: e.target.value }));
                        }}
                        error={!!externalDraftAccessError}
                        helperText={externalDraftAccessError || undefined}
                        InputProps={{ startAdornment: <LinkIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: '1rem' }} /> }}
                      />
                    ) : (
                      <Box>
                        <input
                          type="file"
                          hidden
                          ref={el => { externalDraftFileRef.current = el; }}
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) uploadDraftFile(file, 'external');
                            e.target.value = '';
                          }}
                        />
                        <Box
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') externalDraftFileRef.current?.click(); }}
                          onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                          onDrop={e => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (file) uploadDraftFile(file, 'external');
                          }}
                          onClick={() => externalDraftFileRef.current?.click()}
                          sx={{
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            py: 3,
                            px: 2,
                            textAlign: 'center',
                            cursor: uploadingField === 'external-draft' ? 'wait' : 'pointer',
                            bgcolor: 'background.paper',
                            '&:hover': { borderColor: alpha(SEARCE_COLORS.primary, 0.5) },
                          }}
                        >
                          {uploadingField === 'external-draft' ? (
                            <CircularProgress size={28} />
                          ) : (
                            <>
                              <CloudUploadIcon sx={{ fontSize: 36, color: 'action.active', mb: 0.5 }} />
                              <Typography variant="body2" color="text.secondary">
                                Click to choose file or drag and drop
                              </Typography>
                            </>
                          )}
                        </Box>
                        {externalDraft.url.trim() ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }} title={externalDraft.url}>
                            {referenceLabel(externalDraft.url)}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ flex: 1, minWidth: 200, fontWeight: 600, lineHeight: 1.4 }}
                  >
                    {DOC_SHARE_SERVICE_ACCOUNT_HINT}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button variant="outlined" size="small" onClick={cancelExternalAdd} disabled={checkingDocAccess === 'external'}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={
                        !externalDraft.name.trim()
                        || !externalDraft.url.trim()
                        || uploadingField === 'external-draft'
                        || checkingDocAccess === 'external'
                      }
                      onClick={commitExternalDraft}
                      startIcon={checkingDocAccess === 'external' ? <CircularProgress size={14} color="inherit" /> : undefined}
                      sx={{ bgcolor: SEARCE_COLORS.primary, '&:hover': { bgcolor: SEARCE_COLORS.primaryDark } }}
                    >
                      {checkingDocAccess === 'external' ? 'Checking access…' : 'Add document'}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            )}
          </Grid>

          {/* Marketplace */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={form.marketplace_listing_status} onChange={e => updateField('marketplace_listing_status', e.target.checked)} />}
              label="Marketplace Listing"
            />
          </Grid>
          {form.marketplace_listing_status && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required label="Marketplace URL"
                value={form.marketplace_url}
                onChange={e => updateField('marketplace_url', e.target.value)}
                error={!!formErrors.marketplace_url}
                helperText={formErrors.marketplace_url}
              />
            </Grid>
          )}

          {/* Pricing / Gitlab / PR Code */}
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Pricing Model" value={form.pricing_model} onChange={e => updateField('pricing_model', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Gitlab Repo URL" value={form.gitlab_repo_url} onChange={e => updateField('gitlab_repo_url', e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="PR Code (SFDC)" value={form.pr_code} onChange={e => updateField('pr_code', e.target.value)} />
          </Grid>

          {/* Team Members */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Team Members *</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              {form.team_members.map((email, i) => (
                <Chip key={i} label={email} onDelete={() => removeTeamMember(i)} size="small" icon={<EmailIcon />} />
              ))}
            </Box>
            <Collapse in={showTeamMemberInput}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <Autocomplete<UserAutocomplete, false, false, true>
                  freeSolo
                  sx={{ flex: 1, minWidth: 0 }}
                  options={teamAutocompleteOptions}
                  filterOptions={(opts) => opts}
                  inputValue={teamMemberInput}
                  value={null}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'reset') {
                      setTeamMemberInputQuery('');
                      return;
                    }
                    setTeamMemberInputQuery(value);
                  }}
                  onChange={(_, value) => {
                    const email =
                      typeof value === 'string'
                        ? value.trim()
                        : value?.email?.trim() ?? '';
                    if (email && EMAIL_REGEX.test(email)) {
                      addTeamMember(email, setTeamMemberInputQuery);
                    }
                  }}
                  loading={teamMemberSuggestLoading}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.email
                  }
                  loadingText="Searching…"
                  renderOption={(props, option) => (
                    <li {...props} key={typeof option === 'string' ? option : option.id}>
                      {typeof option === 'string' ? (
                        option
                      ) : (
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" component="span">
                            {option.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span">
                            {option.name}
                          </Typography>
                        </Box>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      placeholder="member@searce.com"
                      error={!!teamMemberSuggestError}
                      helperText={teamMemberSuggestError ?? undefined}
                      InputProps={{
                        ...params.InputProps,
                      }}
                      onKeyDown={(e) => {
                        params.inputProps?.onKeyDown?.(e as React.KeyboardEvent<HTMLInputElement>);
                        if (e.key === 'Enter') {
                          const v = teamMemberInput.trim();
                          if (!v) return;
                          e.preventDefault();
                          const added = addTeamMembersFromInput(v, setTeamMemberInputQuery);
                          if (added === 0 && EMAIL_REGEX.test(v)) {
                            addTeamMember(v, setTeamMemberInputQuery);
                          }
                        }
                      }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData('text') ?? '';
                        const added = addTeamMembersFromInput(pasted, setTeamMemberInputQuery);
                        if (added > 0) {
                          e.preventDefault();
                        }
                      }}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 0.5, flexShrink: 0 }}
                  onClick={() => {
                    const v = teamMemberInput.trim();
                    const added = addTeamMembersFromInput(v, setTeamMemberInputQuery);
                    if (added === 0) addTeamMember(v, setTeamMemberInputQuery);
                  }}
                >
                  Add
                </Button>
              </Box>
            </Collapse>
            <Button size="small" startIcon={showTeamMemberInput ? <ExpandLessIcon /> : <AddIcon />} onClick={() => setShowTeamMemberInput(v => !v)}>
              {showTeamMemberInput ? 'Collapse' : 'Add Member'}
            </Button>
            {formErrors.team_members && (
              <Typography variant="caption" color="error" display="block">{formErrors.team_members}</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          p: 2.5,
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            flex: '1 1 220px',
            minWidth: 0,
            borderLeft: '3px solid',
            borderColor: 'error.main',
            pl: 1.5,
            py: 0.25,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            Please get it reviewed from Managers/Squad Lead before submitting
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: { xs: 0, sm: 'auto' } }}>
          <Button onClick={handleClose} variant="outlined" disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit} variant="contained" disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} /> : null}
          >
            {submitting ? 'Submitting...' : 'Submit Asset'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitAssetDialog;
